import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api, ApiError, getInviteCode, isAuthenticated } from "../lib/api";
import type { ChatMessage as ChatMessageType } from "../lib/types";
import Layout from "../components/Layout";
import ChatMessage, { TypingIndicator } from "../components/ChatMessage";
import ChatInput from "../components/ChatInput";
import QuickFeedbackButton from "../components/QuickFeedbackButton";
import UpsellBanner from "../components/UpsellBanner";
import PipelineReview from "../components/PipelineReview";
import { t, toLanguageCode } from "../i18n/translations";
import type { Language } from "../i18n/translations";

/* ------------------------------------------------------------------ */
/* Client-side assessment detection (safety net for backend failures)   */
/* ------------------------------------------------------------------ */

const ASSESSMENT_MARKERS = [
  "VIABILITY ASSESSMENT", "AVALIACAO DE VIABILIDADE", "AVALIAÇÃO DE VIABILIDADE",
  "FINAL ASSESSMENT", "AVALIACAO FINAL", "AVALIAÇÃO FINAL",
  "CONFIRMED RISKS", "RISCOS CONFIRMADOS",
  "DO NOT PROCEED", "NAO PROSSEGUIR", "NÃO PROSSEGUIR",
  "PROCEED WITH CONDITIONS", "PROSSEGUIR COM CONDICOES", "PROSSEGUIR COM CONDIÇÕES",
  "OVERALL ASSESSMENT",
];

function looksLikeAssessment(text: string): boolean {
  const upper = text.toUpperCase();
  let found = 0;
  for (const m of ASSESSMENT_MARKERS) {
    if (upper.includes(m.toUpperCase())) found++;
  }
  return found >= 2;
}

function isReportComplete(text: string): boolean {
  const upper = text.toUpperCase();
  const hasExecSummary =
    upper.includes("EXECUTIVE SUMMARY") ||
    upper.includes("SUMARIO EXECUTIVO") ||
    upper.includes("SUMÁRIO EXECUTIVO");
  const dimPattern = /DIMENS[AÃ]O?\s*\d|DIMENSION\s*\d/gi;
  const hasDimensions = (text.match(dimPattern) || []).length >= 5;
  const hasConclusion =
    upper.includes("CONFIRMED RISKS") ||
    upper.includes("RISCOS CONFIRMADOS") ||
    upper.includes("RECOMMENDED NEXT STEPS") ||
    upper.includes("PROXIMOS PASSOS") ||
    upper.includes("PRÓXIMOS PASSOS");
  return hasExecSummary && hasDimensions && hasConclusion;
}

/** Detect synthesis signal in AI message.
 *  Signal must be the primary content — strip the marker and check remaining text is minimal. */
function hasSynthesisSignal(text: string): boolean {
  if (!text.includes("SECTION_MARKER: PATTERN_ANALYSIS")) return false;
  const stripped = text.replace(/<!--\s*SECTION_MARKER:\s*PATTERN_ANALYSIS\s*-->/g, "").trim();
  return stripped.length < 200;
}

/** Strip synthesis signal markers from display text */
function cleanAiMessage(text: string): string {
  return text
    .replace(/<!--\s*SECTION_MARKER:\s*PATTERN_ANALYSIS\s*-->/g, "")
    .trim();
}

export default function Chat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectParam = searchParams.get("project");
  const nameParam = searchParams.get("name");
  const langParam = searchParams.get("lang") || localStorage.getItem("architect-language") || "English";
  const feedbackParam = searchParams.get("feedback") === "true";

  const lang: Language = toLanguageCode(langParam);

  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [briefReady, setBriefReady] = useState(false);
  const [briefContent, setBriefContent] = useState<string | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [downloadingBrief, setDownloadingBrief] = useState(false);
  const [downloadingAssessment, setDownloadingAssessment] = useState(false);
  const [feedbackComplete, setFeedbackComplete] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState<"phase0" | "conversation" | "assessment" | null>(null);

  // Paywall state (Specs B/C/D)
  const [interrogationComplete, setInterrogationComplete] = useState(false);
  const [partialAssessment, setPartialAssessment] = useState<string | null>(null);
  const [fullAssessment, setFullAssessment] = useState<string | null>(null);
  const [assessmentPurchased, setAssessmentPurchased] = useState(false);
  const [generatingPartial, setGeneratingPartial] = useState(false);
  const [generatingFull, setGeneratingFull] = useState(false);
  const [pricingInfo, setPricingInfo] = useState<{ price: number; is_pilot: boolean } | null>(null);
  const [paidBannerDismissed, setPaidBannerDismissed] = useState(false);

  // Business Plan ($249) state
  const [customerTier, setCustomerTier] = useState<string>("free");
  const [projectPhase, setProjectPhase] = useState<string>("");
  const [isArchived, setIsArchived] = useState(false);
  const [bpPaymentPending, setBpPaymentPending] = useState(false);

  // Part 2: Post-download feedback prompt state
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [feedbackPromptDismissed, setFeedbackPromptDismissed] = useState(false);

  // Part 4: Return-visit nudge state
  const [showReturnNudge, setShowReturnNudge] = useState(false);

  // Tiered feedback gating state
  const [pilotTier, setPilotTier] = useState<number | null>(null);
  const [showTier2Modal, setShowTier2Modal] = useState(false);
  const [tier2ModalDismissed, setTier2ModalDismissed] = useState(false);

  // Inline feedback (replaces separate feedback page for auto-start)
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [feedbackMessages, setFeedbackMessages] = useState<ChatMessageType[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const feedbackStartingRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Auth guard + fetch runs remaining
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/", { replace: true });
      return;
    }
    const code = getInviteCode();
    if (code) {
      api.verifyCode(code).then((res) => {
        if (!res.valid) {
          navigate("/", { replace: true });
        } else {
          setPilotTier(res.pilot_tier ?? null);
        }
      }).catch(() => {
        // Backend might be cold -- don't redirect, just continue
      });
    }
  }, [navigate]);

  // Resume existing project or feedback
  useEffect(() => {
    if (!projectParam) return;

    setResumeLoading(true);

    if (feedbackParam) {
      // Load feedback conversation + project detail for download banner
      setProjectId(projectParam);
      Promise.all([
        api.getFeedback(projectParam),
        api.getProject(projectParam).catch(() => null),
      ]).then(([fbRes, detailRes]) => {
        setMessages(fbRes.messages);
        if (fbRes.status === "feedback_complete") setFeedbackComplete(true);
        if (detailRes?.full_assessment) setFullAssessment(detailRes.full_assessment);
        if (detailRes?.pilot_tier != null) setPilotTier(detailRes.pilot_tier);
        if (detailRes?.assessment_purchased) setAssessmentPurchased(true);
        setInterrogationComplete(true);
      }).catch(() => {}).finally(() => setResumeLoading(false));
    } else {
      // Load main project conversation
      api.getProject(projectParam)
        .then((detail) => {
          setProjectId(detail.project_id);
          setMessages(detail.messages);

          const phase = detail.phase || "";

          console.log("[Banner2 Debug] API response: " + JSON.stringify({
            phase,
            has_partial: !!detail.partial_assessment,
            has_full: !!detail.full_assessment,
            assessment_purchased: detail.assessment_purchased,
            has_brief: !!detail.brief,
          }, null, 2));

          // Restore paywall state from project
          if (detail.partial_assessment) {
            setPartialAssessment(detail.partial_assessment);
          }
          if (detail.assessment_purchased) {
            setAssessmentPurchased(true);
          }
          if (detail.pilot_tier != null) {
            setPilotTier(detail.pilot_tier);
          }
          if (detail.customer_tier) {
            setCustomerTier(detail.customer_tier);
          }
          if (detail.archived_at) {
            setIsArchived(true);
          }
          setProjectPhase(phase);

          // Resolve full assessment from all possible sources:
          // 1. full_assessment column (paywall flow)
          // 2. brief column when purchased (legacy with purchase)
          // 3. brief column when phase indicates completion (any flow)
          // 4. brief column for legacy "brief_ready" projects (pre-paywall)
          //
          // IMPORTANT: paid_interrogation must NOT resolve brief as full assessment.
          // The user has purchased but interrogation is still active — the brief
          // column contains the free-tier closing message, not a full assessment.
          const hasAssessmentContent =
            phase === "assessment_complete" ||
            phase === "assessment_purchased" ||
            phase === "brief_ready" ||
            (detail.assessment_purchased && phase !== "paid_interrogation");

          const resolvedFullAssessment = detail.full_assessment
            || (hasAssessmentContent && detail.brief)
            || null;

          if (resolvedFullAssessment) {
            setFullAssessment(resolvedFullAssessment);
            setAssessmentPurchased(true);
            setInterrogationComplete(true);

            // If tier 1/admin with full assessment, load existing feedback inline
            if (detail.pilot_tier === 1) {
              api.getFeedback(detail.project_id).then((fb) => {
                if (fb.messages?.length > 0) {
                  setFeedbackMessages(fb.messages);
                  setFeedbackMode(true);
                  if (fb.status === "feedback_complete") setFeedbackComplete(true);
                }
              }).catch(() => { /* no feedback yet */ });
            }
          }

          if (
            phase === "interrogation_complete" ||
            phase === "partial_assessment_generated" ||
            phase === "assessment_purchased" ||
            phase === "assessment_complete" ||
            phase === "brief_ready" ||
            phase === "paid_interrogation_complete"
          ) {
            setInterrogationComplete(true);
          }
          // paid_interrogation: chat remains active (interrogationComplete stays false)
          //
          // Auto-send first paid message if no paid-phase AI response exists yet.
          // After auto-send, the backend stores the AI response but strips the
          // "continue" user message.  So on reload the last message will be from
          // the assistant (= paid AI already responded, don't re-send).
          // The free-tier conversation ends with a user message (closing stripped),
          // so lastRole === "user" means no paid response yet.
          const lastRole = detail.messages.length > 0
            ? detail.messages[detail.messages.length - 1].role
            : null;
          if (
            phase === "paid_interrogation" &&
            detail.assessment_purchased &&
            lastRole === "user"
          ) {
            // No user message after purchase — auto-send "continue"
            setIsLoading(true);
            setLoadingPhase("conversation");
            api.sendMessage(detail.project_id, "continue").then((contRes) => {
              if (contRes.ai_message) {
                const displayMsg = cleanAiMessage(contRes.ai_message);
                setMessages((prev) => [...prev, { role: "assistant", content: displayMsg }]);
              }
            }).catch(() => {
              // Non-fatal — user can type manually
            }).finally(() => {
              setIsLoading(false);
              setLoadingPhase(null);
            });
          }

          // If purchased but full assessment missing, poll (and recover if needed)
          // Covers: assessment_purchased (just paid), paid_interrogation_complete (generating),
          // assessment_complete (content lost), interrogation_complete (stuck — S-009)
          if (
            detail.assessment_purchased &&
            !resolvedFullAssessment &&
            (phase === "assessment_purchased" || phase === "paid_interrogation_complete" || phase === "assessment_complete" || phase === "interrogation_complete")
          ) {
            setGeneratingFull(true);
            // Recovery: trigger re-generation for stuck or lost-content states
            if (phase === "assessment_complete" || phase === "interrogation_complete") {
              api.generateFullAssessment(detail.project_id).catch(() => {});
            }
            const pollInterval = setInterval(() => {
              api.getFullAssessment(detail.project_id).then((res) => {
                if (res.status === "ready" && res.full_assessment) {
                  setFullAssessment(res.full_assessment);
                  setGeneratingFull(false);
                  clearInterval(pollInterval);
                }
              }).catch(() => {});
            }, 3000);
            setTimeout(async () => {
              clearInterval(pollInterval);
              try {
                const res = await api.getFullAssessment(detail.project_id);
                if (res.status === "ready" && res.full_assessment) {
                  setFullAssessment(res.full_assessment);
                }
              } catch { /* ignore */ }
              setGeneratingFull(false);
            }, 600000);
          }

          // Fetch pricing info for Banner 2
          api.getPricing(detail.project_id)
            .then(setPricingInfo)
            .catch(() => {});

          // Legacy brief detection — only for active interrogation projects
          // that haven't produced any assessment yet.
          if (!resolvedFullAssessment && !detail.partial_assessment && phase === "interrogation") {
            let briefText = detail.brief;
            if (!briefText && detail.messages.length > 0) {
              const lastAssistant = [...detail.messages].reverse().find((m) => m.role === "assistant");
              if (lastAssistant && /VIABILITY ASSESSMENT|OVERALL ASSESSMENT|DO NOT PROCEED|PROCEED WITH CAUTION/i.test(lastAssistant.content)) {
                const sectionMatch = lastAssistant.content.match(/#+\s*\d+-DIMENSION VIABILITY ASSESSMENT\b/i);
                briefText = sectionMatch
                  ? lastAssistant.content.slice(sectionMatch.index)
                  : lastAssistant.content;
              }
            }
            if (briefText) {
              setBriefReady(true);
              setBriefContent(briefText);
            }
          }

          // Return-visit nudge -- only for assessment_complete, not partial_assessment_generated
          if (
            phase === "assessment_complete" &&
            !localStorage.getItem(`nudge-dismissed-${detail.project_id}`)
          ) {
            setShowReturnNudge(true);
            api.logEvent(detail.project_id, "return_nudge_shown");
          }

          // If project has a completed run, navigate to artifacts
          const completedRun = detail.runs.find((r) => r.status === "completed");
          if (completedRun) {
            navigate(`/artifacts?run_id=${completedRun.run_id}`, { replace: true });
          }

          // Handle payment=success redirect from Stripe
          const paymentStatus = searchParams.get("payment");
          if (paymentStatus === "success" && !detail.assessment_purchased) {
            // Poll for purchase confirmation (webhook may arrive shortly)
            const pollInterval = setInterval(() => {
              api.getProject(detail.project_id).then((proj) => {
                if (proj.assessment_purchased) {
                  setAssessmentPurchased(true);
                  if (proj.customer_tier) setCustomerTier(proj.customer_tier);
                  if (proj.phase === "paid_interrogation") {
                    setInterrogationComplete(false); // Re-enable chat
                  } else if (proj.full_assessment) {
                    setFullAssessment(proj.full_assessment);
                  }
                  clearInterval(pollInterval);
                }
              }).catch(() => {});
            }, 3000);
            // Stop polling after 10 minutes
            setTimeout(() => { clearInterval(pollInterval); }, 600000);
          }

          // Handle payment=bp_success redirect from Stripe ($249 Business Plan)
          if (paymentStatus === "bp_success" && detail.customer_tier !== "business_plan") {
            setBpPaymentPending(true);
            api.logEvent(detail.project_id, "bp_payment_polling_started");
            const bpPollInterval = setInterval(() => {
              api.getProject(detail.project_id).then((proj) => {
                if (proj.customer_tier === "business_plan") {
                  setCustomerTier("business_plan");
                  setProjectPhase(proj.phase || "business_case_generating");
                  setBpPaymentPending(false);
                  clearInterval(bpPollInterval);
                  api.logEvent(detail.project_id, "bp_payment_confirmed_via_poll");
                }
              }).catch(() => {});
            }, 2000);
            setTimeout(() => { clearInterval(bpPollInterval); }, 600000);
          }
        })
        .catch(() => {
          // Project not found -- start fresh
        })
        .finally(() => setResumeLoading(false));
    }
  }, [projectParam, feedbackParam, navigate, searchParams]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isLoading, briefReady, feedbackComplete, loadingPhase, showFeedbackPrompt, partialAssessment, fullAssessment, generatingPartial, generatingFull, feedbackMessages]);

  // Client-side fallback: detect assessment in messages if backend didn't set briefReady
  // Skip when paywall flow is active (interrogationComplete) to avoid interfering with Banner 1
  useEffect(() => {
    if (briefReady || feedbackParam || messages.length === 0 || interrogationComplete) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === "assistant" && looksLikeAssessment(lastMsg.content)) {
      const sectionRe = /#+\s*(\d+-DIMENSION VIABILITY ASSESSMENT|AVALIA[CÇ][AÃ]O DE VIABILIDADE)\b/i;
      const match = lastMsg.content.match(sectionRe);
      const extracted = match ? lastMsg.content.slice(match.index) : lastMsg.content;
      setBriefReady(true);
      setBriefContent(extracted);
      if (projectId) api.logEvent(projectId, "assessment_detected_client_fallback");
    }
  }, [messages, briefReady, feedbackParam, projectId, interrogationComplete]);

  // Spec E: Timed feedback prompt (15s after FULL assessment ready, not partial)
  // Skip for Tier 1 users (they get auto-start instead)
  // Skip during Business Plan pipeline phases — feedback is irrelevant while generating/reviewing artifacts
  const inPipelinePhase = [
    "business_case_generating", "business_case_review",
    "product_spec_generating", "product_spec_review",
    "architecture_generating", "architecture_review",
    "epic_plan_generating", "epic_plan_review",
    "pipeline_complete",
  ].includes(projectPhase);

  useEffect(() => {
    if (pilotTier === 1) return;
    if (inPipelinePhase) return;
    if (!fullAssessment || feedbackParam || feedbackPromptDismissed || showFeedbackPrompt) return;
    const timer = setTimeout(() => {
      setShowFeedbackPrompt(true);
      if (projectId) api.logEvent(projectId, "feedback_prompt_shown");
    }, 15000);
    return () => clearTimeout(timer);
  }, [fullAssessment, feedbackParam, feedbackPromptDismissed, showFeedbackPrompt, projectId, pilotTier, inPipelinePhase]);

  // Tier 1: Auto-start feedback 4s after full assessment ready
  useEffect(() => {
    if (!projectId || pilotTier !== 1 || !fullAssessment || feedbackMode || feedbackComplete) return;
    if (feedbackParam) return; // Already on feedback page
    if (inPipelinePhase) return; // Suppress during Business Plan pipeline

    const timer = setTimeout(async () => {
      try {
        await api.startFeedback(projectId);
        const fb = await api.getFeedback(projectId);
        if (fb.messages?.length > 0) {
          setFeedbackMessages(fb.messages);
          setFeedbackMode(true);
          api.logEvent(projectId, "feedback_auto_started_inline");
        }
      } catch { /* already active or failed */ }
    }, 4000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pilotTier, fullAssessment, feedbackMode, feedbackComplete, feedbackParam, inPipelinePhase]);

  async function handleSend(text: string) {
    setSendError(null);
    const userMsg: ChatMessageType = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      if (feedbackMode && projectId) {
        // Inline feedback mode -- route to feedback endpoint
        setFeedbackMessages(prev => [...prev, userMsg]);
        const res = await api.sendFeedbackMessage(projectId, text);
        setFeedbackMessages(prev => [...prev, { role: "assistant", content: res.message }]);
        if (res.feedback_complete) {
          setFeedbackComplete(true);
        }
        return;
      }
      if (feedbackParam && projectId) {
        // Feedback mode -- send to feedback endpoint
        const res = await api.sendFeedbackMessage(projectId, text);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.message },
        ]);
        if (res.feedback_complete) {
          setFeedbackComplete(true);
        }
      } else if (!projectId) {
        // First message -- create project (Phase 0 research takes 10-20s)
        setLoadingPhase("phase0");
        try {
          const res = await api.createProject(text, nameParam || undefined, langParam);
          setProjectId(res.project_id);
          navigate(`/chat?project=${res.project_id}`, { replace: true });
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: res.ai_message },
          ]);
        } finally {
          setLoadingPhase(null);
        }
      } else {
        // Subsequent messages -- show progress based on conversation round
        const userMsgCount = messages.filter((m) => m.role === "user").length;
        // During paid interrogation, always show "conversation" loading (not "assessment")
        const isPaidChat = assessmentPurchased && !interrogationComplete && !fullAssessment;
        setLoadingPhase(
          isPaidChat ? "conversation" : userMsgCount >= 4 ? "assessment" : "conversation"
        );
        // Dismiss the paid-interrogation transition banner on first send
        if (isPaidChat) setPaidBannerDismissed(true);

        Sentry.setContext("conversation", {
          projectId,
          messageRound: userMsgCount + 1,
          messageLength: text.length,
        });

        const res = await api.sendMessage(projectId, text);

        // Clean synthesis signal from display (ai_message may be null for forced synthesis)
        if (res.ai_message) {
          const displayMessage = cleanAiMessage(res.ai_message);
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: displayMessage },
          ]);
        }

        // Check for synthesis signal (new paywall flow)
        if (res.phase === "interrogation_complete" || (res.ai_message && hasSynthesisSignal(res.ai_message))) {
          setInterrogationComplete(true);
          // Clear any legacy brief set by client-side fallback during RESOLVE
          setBriefReady(false);
          setBriefContent(null);
          api.getPricing(projectId).then(setPricingInfo).catch(() => {});
        }

        // Paid interrogation complete — full assessment being generated
        if (res.phase === "paid_interrogation_complete") {
          setInterrogationComplete(true);
          setCustomerTier("assessment");  // ensure upsell banner shows
          setGeneratingFull(true);
          // Poll for full assessment
          const pollInterval = setInterval(() => {
            api.getFullAssessment(projectId).then((r) => {
              if (r.status === "ready" && r.full_assessment) {
                setFullAssessment(r.full_assessment);
                setGeneratingFull(false);
                clearInterval(pollInterval);
              }
            }).catch(() => {});
          }, 3000);
          setTimeout(() => { clearInterval(pollInterval); setGeneratingFull(false); }, 600000);
        }

        // Legacy brief detection — skip if interrogation just completed (avoids
        // re-setting briefContent that was cleared above in the same response).
        if (
          res.brief_ready &&
          res.brief_content &&
          res.phase !== "interrogation_complete" &&
          !(res.ai_message && hasSynthesisSignal(res.ai_message))
        ) {
          setBriefReady(true);
          setBriefContent(res.brief_content);
        }
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: "chat", action: "send_message" },
        extra: { projectId, messageLength: text.length },
      });
      const isRetryable =
        (err instanceof ApiError && err.retry) ||
        (err instanceof TypeError); // network error (fetch failed)
      if (isRetryable) {
        // Remove the user message that wasn't processed; show inline retry
        setMessages((prev) => prev.slice(0, -1));
        setSendError(text);
      } else {
        const errMsg =
          err instanceof Error ? err.message : "Something went wrong";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${errMsg}. Please try again.` },
        ]);
      }
    } finally {
      setIsLoading(false);
      setLoadingPhase(null);
    }
  }

  async function handleGeneratePartialAssessment() {
    if (!projectId || generatingPartial) return;
    setGeneratingPartial(true);
    if (projectId) api.logEvent(projectId, "assessment_generation_started");
    try {
      const res = await api.generatePartialAssessment(projectId);
      setPartialAssessment(res.partial_assessment);
      if (projectId) api.logEvent(projectId, "assessment_generation_completed");
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Generation failed";
      setPartialAssessment(`Error: ${errMsg}. Please try again.`);
    } finally {
      setGeneratingPartial(false);
    }
  }

  async function handlePurchaseAssessment() {
    if (!projectId) return;
    if (projectId) api.logEvent(projectId, "paywall_purchase_clicked");
    const res = await api.purchaseAssessment(projectId);
    if (res.status === "purchased") {
      setAssessmentPurchased(true);
      setCustomerTier("assessment");
      setInterrogationComplete(false);  // Re-enable chat for paid interrogation
      api.logEvent(projectId, "paywall_purchase_completed");
      // Auto-send a "continue" message so the interrogator picks up immediately.
      // The backend injects [CONTINUATION:] context on the first paid message,
      // so the AI will naturally resume from the free-tier conversation.
      // We don't display "continue" as a user message — only the AI response.
      setIsLoading(true);
      setLoadingPhase("conversation");
      try {
        const contRes = await api.sendMessage(projectId, "continue");
        if (contRes.ai_message) {
          const displayMessage = cleanAiMessage(contRes.ai_message);
          setMessages((prev) => [...prev, { role: "assistant", content: displayMessage }]);
        }
      } catch {
        // Non-fatal — user can still type manually
      } finally {
        setIsLoading(false);
        setLoadingPhase(null);
      }
    } else if (res.status === "generating") {
      // Legacy: poll for completion
      setGeneratingFull(true);
      setAssessmentPurchased(true);
      setCustomerTier("assessment");
      const pollInterval = setInterval(async () => {
        try {
          const full = await api.getFullAssessment(projectId);
          if (full.status === "ready" && full.full_assessment) {
            setFullAssessment(full.full_assessment);
            setGeneratingFull(false);
            clearInterval(pollInterval);
            api.logEvent(projectId, "paywall_purchase_completed");
          }
        } catch {
          // Keep polling
        }
      }, 3000);
      setTimeout(async () => {
        clearInterval(pollInterval);
        try {
          const full = await api.getFullAssessment(projectId);
          if (full.status === "ready" && full.full_assessment) {
            setFullAssessment(full.full_assessment);
          }
        } catch { /* ignore */ }
        setGeneratingFull(false);
      }, 600000);
    } else if (res.status === "payment_required" && res.checkout_url) {
      // Redirect to Stripe
      window.location.href = res.checkout_url;
    } else if (res.status === "already_purchased") {
      setAssessmentPurchased(true);
      setCustomerTier("assessment");
      const full = await api.getFullAssessment(projectId);
      if (full.full_assessment) {
        setFullAssessment(full.full_assessment);
      }
    }
  }

  async function handlePurchaseBusinessPlan() {
    if (!projectId) return;
    api.logEvent(projectId, "business_plan_purchase_clicked");
    const res = await api.purchaseBusinessPlan(projectId);
    if (res.status === "unlocked") {
      // Admin/free bypass — instant unlock
      setCustomerTier("business_plan");
      setProjectPhase("business_case_generating");
      api.logEvent(projectId, "business_plan_unlocked");
    } else if (res.status === "checkout" && res.redirect) {
      // Redirect to Stripe
      window.location.href = res.redirect;
    }
  }

  function handleExportChat() {
    const date = new Date().toISOString().slice(0, 16).replace("T", " ");
    const title = feedbackParam
      ? t("chat.export.title.feedback", lang)
      : t("chat.export.title.main", lang);
    let md = `# Architect Session -- ${title}\n**Date:** ${date}\n**Mode:** Business\n\n---\n\n## Conversation\n\n`;
    for (const msg of messages) {
      const speaker = msg.role === "user" ? "You" : "Architect";
      md += `**${speaker}:**\n${msg.content}\n\n`;
    }
    downloadText("architect-session.md", md);
  }

  async function _doDownload() {
    if (!projectId) return;
    setDownloadingAssessment(true);
    api.logEvent(projectId, "assessment_downloaded");
    try {
      // Try PDF first, fall back to markdown
      await api.downloadAssessmentPdf(projectId);
    } catch {
      const raw = fullAssessment || briefContent;
      const content = raw?.replace(/<!--\s*(?:SECTION_MARKER|END_SECTION_MARKER):\s*\w+\s*-->\n?/g, "");
      if (content) {
        downloadText("viability-assessment.md", content);
      } else {
        await api.downloadAssessmentMd(projectId).catch(() => {});
      }
    } finally {
      setDownloadingAssessment(false);
    }
    if (!feedbackPromptDismissed && pilotTier !== 1) {
      setShowFeedbackPrompt(true);
      api.logEvent(projectId, "feedback_prompt_shown");
    }
  }

  function handleDownloadAssessment() {
    if (!projectId) return;
    // Tier 2 soft gate: show interstitial once per session
    if (pilotTier === 2 && !tier2ModalDismissed && !feedbackParam && !feedbackComplete) {
      setShowTier2Modal(true);
      if (projectId) api.logEvent(projectId, "tier2_interstitial_shown");
      return;
    }
    _doDownload();
  }

  function handleViewExecSummary() {
    if (!projectId) return;
    api.logEvent(projectId, "exec_summary_viewed");
    // Try PDF first, fall back to HTML, then plain text
    api.downloadExecSummaryPdf(projectId).catch(() => {
      api.openExecSummaryHtml(projectId).catch(() => {
        const content = fullAssessment || briefContent;
        if (content) {
          const execMatch = content.match(/##\s*(?:EXECUTIVE SUMMARY|RESUMO EXECUTIVO)\s*\n+([\s\S]*?)(?=\n##)/i);
          const extracted = execMatch ? execMatch[0] : content.slice(0, 2000);
          const blob = new Blob([extracted], { type: "text/plain;charset=utf-8" });
          window.open(URL.createObjectURL(blob), "_blank");
        }
      });
    });
  }

  async function handleStartFeedback() {
    if (!projectId || feedbackStartingRef.current) return;
    feedbackStartingRef.current = true;
    try {
      api.logEvent(projectId, "feedback_started");
      await api.startFeedback(projectId);
    } catch {
      // Feedback may already be active -- navigate anyway
    }
    navigate(`/chat?project=${projectId}&feedback=true`);
  }

  function handleDismissFeedbackPrompt() {
    setShowFeedbackPrompt(false);
    setFeedbackPromptDismissed(true);
    if (projectId) api.logEvent(projectId, "feedback_dismissed");
  }

  function handleDismissReturnNudge() {
    setShowReturnNudge(false);
    if (projectId) {
      localStorage.setItem(`nudge-dismissed-${projectId}`, "1");
      api.logEvent(projectId, "return_nudge_dismissed");
    }
  }


  if (resumeLoading) {
    return (
      <Layout showHeader>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-500 text-sm">{t("chat.restoring", lang)}</p>
        </div>
      </Layout>
    );
  }

  const inputDisabled = isLoading || feedbackComplete || generatingFull || (interrogationComplete && !feedbackParam && !feedbackMode);
  const showBanner1 = interrogationComplete && !partialAssessment && !fullAssessment && !briefReady && !generatingFull;
  const paidInterrogationActive = assessmentPurchased && !interrogationComplete && !fullAssessment;
  const showPartialAssessment = !!partialAssessment && !fullAssessment && !paidInterrogationActive && !generatingFull;
  const showFullAssessment = !!fullAssessment;
  const showBanner2 = showPartialAssessment && !assessmentPurchased && !generatingFull;
  const parsedSkeletons = partialAssessment ? parsePartialAssessment(partialAssessment).skeletons.map(s => s.name) : [];

  // Second upsell: show $249/$1999 after full assessment is visible, only for assessment-tier customers
  // Hide when payment is pending (user just returned from Stripe, waiting for webhook confirmation)
  const showBusinessPlanUpsell = showFullAssessment && customerTier === "assessment" && !isLoading && !bpPaymentPending;

  // Pipeline phase placeholders
  const isPipelineGenerating = [
    "business_case_generating", "product_spec_generating",
    "architecture_generating", "epic_plan_generating",
  ].includes(projectPhase);
  const isPipelineReview = [
    "business_case_review", "product_spec_review",
    "architecture_review", "epic_plan_review",
  ].includes(projectPhase);
  const isPipelineComplete = projectPhase === "pipeline_complete";

  console.log("[Banner2 Debug] render: " + JSON.stringify({
    showBanner2,
    showPartialAssessment,
    assessmentPurchased,
    generatingFull,
    projectId: projectId || "(empty)",
    hasPartial: !!partialAssessment,
    hasFullAssessment: !!fullAssessment,
    briefReady,
    interrogationComplete,
    pricingInfo,
  }, null, 2));

  return (
    <Layout
      showHeader
      onExport={messages.length > 0 ? handleExportChat : undefined}
    >
      {/* Archived project banner */}
      {isArchived && (
        <div className="flex-shrink-0 bg-amber-600/10 border-b border-amber-500/20 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
            <span className="text-amber-300 text-sm">
              This project is archived. Reactivate it from your dashboard to continue.
            </span>
            <button
              onClick={() => navigate("/dashboard")}
              className="text-sm font-medium text-amber-200 hover:text-white transition-colors whitespace-nowrap"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Feedback banner */}
      {feedbackParam && (
        <div className="flex-shrink-0 bg-blue-600/10 border-b border-blue-500/20 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center gap-2">
            <span className="text-blue-400 text-sm font-medium">
              {t("feedback.banner", lang)}
            </span>
          </div>
        </div>
      )}

      {/* Part 4: Return-visit nudge banner (skip for Tier 1 -- they get auto-start) */}
      {showReturnNudge && !feedbackParam && pilotTier !== 1 && (
        <div className="flex-shrink-0 bg-amber-600/10 border-b border-amber-500/20 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
            <span className="text-amber-300 text-sm">
              {t("nudge.message", lang)}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleStartFeedback}
                className="text-sm font-medium text-amber-200 hover:text-white transition-colors"
              >
                {t("nudge.cta", lang)}
              </button>
              <button
                onClick={handleDismissReturnNudge}
                className="text-slate-500 hover:text-slate-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message area */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-6">
        <div className={`mx-auto ${(isPipelineGenerating || isPipelineReview || isPipelineComplete) ? "max-w-6xl" : "max-w-3xl"}`}>
          {messages.length === 0 && !isLoading && !feedbackParam && (
            <div className="text-center py-20">
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                {t("chat.placeholder.title", lang)}
              </h2>
              <p className="text-slate-400 text-sm sm:text-base max-w-md mx-auto">
                {t("chat.placeholder.subtitle", lang)}
              </p>
              <div className="mt-8 max-w-lg mx-auto">
                <p className="text-xs text-slate-500 mb-2">
                  {t("chat.examples.heading", lang)}
                </p>
                <ul className="space-y-1.5 text-xs text-slate-600 italic">
                  <li>&ldquo;{t("chat.examples.1", lang)}&rdquo;</li>
                  <li>&ldquo;{t("chat.examples.2", lang)}&rdquo;</li>
                  <li>&ldquo;{t("chat.examples.3", lang)}&rdquo;</li>
                </ul>
                <hr className="border-slate-700/50 my-4" />
                <div className="space-y-3 text-xs text-slate-600 italic">
                  <p>&ldquo;{t("chat.examples.4", lang)}&rdquo;</p>
                  <hr className="border-slate-700/50" />
                  <p>&ldquo;{t("chat.examples.5", lang)}&rdquo;</p>
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            // Hide assessment content from chat — it's shown via dedicated banners/cards
            if (msg.role === "assistant" && looksLikeAssessment(msg.content)) return null;
            // Hide internal section markers (<!-- ... -->) from interrupted generation
            if (msg.role === "assistant" && /^\s*<!--[\s\S]*-->\s*$/.test(msg.content)) return null;
            return <ChatMessage key={i} role={msg.role} content={msg.content} />;
          })}

          {isLoading && loadingPhase && (
            <ProgressIndicator type={loadingPhase} language={lang} />
          )}
          {isLoading && !loadingPhase && <TypingIndicator />}

          {/* ── Banner 1: Generate Your Assessment ── */}
          {showBanner1 && !isLoading && (
            <div data-testid="banner-generate-assessment" className="bg-slate-800/80 border border-blue-500/30 rounded-2xl p-6 mt-6 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">
                Your interrogation is complete.
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Generate your viability assessment based on everything we discussed.
              </p>
              <button
                onClick={handleGeneratePartialAssessment}
                disabled={generatingPartial}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-medium rounded-xl px-6 py-3 text-sm transition-colors"
              >
                {generatingPartial ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing your interrogation...
                  </>
                ) : (
                  "Generate Assessment"
                )}
              </button>
            </div>
          )}

          {/* ── Partial Assessment Display ── */}
          {showPartialAssessment && !isLoading && (
            <div data-testid="partial-assessment-section">
              <PartialAssessmentCard content={partialAssessment!} />
              {/* Download Brief Assessment button (only before purchase) */}
              {!assessmentPurchased && (
                <div className="flex justify-center mt-3">
                  <button
                    data-testid="download-brief-btn"
                    disabled={downloadingBrief}
                    onClick={async () => {
                      if (projectId) {
                        setDownloadingBrief(true);
                        try {
                          await api.downloadBriefPdf(projectId);
                          api.logEvent(projectId, "brief_assessment_downloaded");
                        } catch {
                          // Fallback to markdown if PDF fails
                          if (partialAssessment) {
                            downloadText("brief-assessment.md", partialAssessment);
                            api.logEvent(projectId, "brief_assessment_downloaded");
                          }
                        } finally {
                          setDownloadingBrief(false);
                        }
                      }
                    }}
                    className="inline-flex items-center gap-2 text-slate-400 hover:text-white disabled:text-slate-600 text-sm transition-colors"
                  >
                    {downloadingBrief ? (
                      <>
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                        Preparing PDF...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Brief Assessment
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Generating Full Assessment ── */}
          {generatingFull && (
            <div data-testid="generating-full-spinner"><FullGenerationProgress /></div>
          )}

          {/* ── Banner 2: Tier Comparison / Purchase ── */}
          {showBanner2 && projectId && (
            <div data-testid="upsell-banner-assessment">
              <UpsellBanner
                projectId={projectId}
                language={lang}
                pricingInfo={pricingInfo}
                showcaseDimension={extractShowcaseDimension(partialAssessment || "")}
                skeletonDimensions={parsedSkeletons}
                onPurchase={handlePurchaseAssessment}
              />
            </div>
          )}

          {/* ── Paid Interrogation Transition Banner ── */}
          {paidInterrogationActive && !paidBannerDismissed && (
            <div data-testid="paid-interrogation-banner" className="bg-blue-900/30 border border-blue-500/20 rounded-xl p-4 mt-4">
              <p className="text-sm text-blue-300">
                Assessment unlocked. The interrogation will now continue to cover dimensions we haven&apos;t investigated yet. This typically takes 15–20 more minutes. Your full 10-dimension assessment will be generated after the extended interrogation completes.
              </p>
            </div>
          )}

          {/* ── Full Assessment Ready Banner ── */}
          {showFullAssessment && !isLoading && (
            <div data-testid="full-assessment-banner" className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-6 mt-6">
              <div className="flex items-center gap-3 mb-3">
                <svg className="w-6 h-6 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="text-lg font-semibold text-white">Your full viability assessment is ready</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                10-dimension analysis &middot; Mathematical models &middot; Competitive dossier &middot; Risk action plans
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="download-assessment-btn"
                  disabled={downloadingAssessment}
                  onClick={handleDownloadAssessment}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:bg-green-600/50 disabled:cursor-wait text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors shadow-lg"
                >
                  {downloadingAssessment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Preparing PDF...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Assessment
                    </>
                  )}
                </button>
                <button
                  data-testid="exec-summary-btn"
                  onClick={handleViewExecSummary}
                  className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors"
                >
                  View Executive Summary
                </button>
              </div>
            </div>
          )}

          {/* ── Second Upsell: $249 Business Plan (after assessment purchased) ── */}
          {showBusinessPlanUpsell && projectId && (
            <div data-testid="business-plan-upsell">
              <UpsellBanner
                projectId={projectId}
                language={lang}
                onPurchaseBusinessPlan={handlePurchaseBusinessPlan}
                hideAssessmentTier
              />
            </div>
          )}

          {/* ── Business Plan payment pending indicator ── */}
          {bpPaymentPending && !isPipelineGenerating && !isPipelineReview && !isPipelineComplete && (
            <div className="bg-slate-800/80 border border-blue-500/20 rounded-2xl p-6 mt-4">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <div>
                  <p className="text-sm font-medium text-white">Payment received -- setting up your pipeline...</p>
                  <p className="text-xs text-slate-400 mt-0.5">This usually takes a few seconds.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Pipeline Review UI ($249 tier) ── */}
          {(isPipelineGenerating || isPipelineReview || isPipelineComplete) && projectId && (
            <div className="mt-6">
              <PipelineReview projectId={projectId} language={lang} />
            </div>
          )}

          {/* Inline feedback conversation (tier 1 auto-start) */}
          {feedbackMode && feedbackMessages.length > 0 && !feedbackParam && (
            <div className="mt-6 border-t border-slate-700/50 pt-4">
              <div className="text-xs text-slate-500 mb-4">Feedback Interview</div>
              {feedbackMessages.map((msg, i) => (
                <ChatMessage key={`fb-${i}`} role={msg.role} content={msg.content} />
              ))}
              {feedbackComplete && (
                <div className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-5 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-semibold text-green-400">Thank you for your feedback!</span>
                  </div>
                  <button
                    disabled={downloadingAssessment}
                    onClick={handleDownloadAssessment}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:bg-green-600/50 disabled:cursor-wait text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors shadow-lg"
                  >
                    {downloadingAssessment ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Preparing PDF...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Assessment
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Download buttons -- only for legacy brief flow (paywall flow uses the ready banner above) */}
          {!feedbackParam && !fullAssessment && !partialAssessment && briefContent && !isLoading && (() => {
            const content = fullAssessment || briefContent || "";
            const complete = isReportComplete(content);
            return (
              <div className="flex flex-col items-center my-4 gap-2">
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    disabled={downloadingAssessment}
                    onClick={handleDownloadAssessment}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:bg-green-600/50 disabled:cursor-wait text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors shadow-lg"
                  >
                    {downloadingAssessment ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Preparing PDF...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t(complete ? "assessment.download" : "assessment.download.partial", lang)}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleViewExecSummary}
                    className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors"
                  >
                    {t("assessment.download.exec", lang)}
                  </button>
                </div>
                {!complete && (
                  <p className="text-xs text-amber-400 max-w-sm text-center">
                    {t("assessment.incomplete.warning", lang)}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Legacy upsell banner -- only for legacy brief_ready flow without paywall */}
          {!feedbackParam && briefReady && !interrogationComplete && !assessmentPurchased && projectId && !isLoading && (
            <UpsellBanner projectId={projectId} language={lang} />
          )}

          {/* Part 2: Post-download feedback prompt (skip for Tier 1 -- they get auto-start) */}
          {showFeedbackPrompt && !feedbackParam && !feedbackPromptDismissed && pilotTier !== 1 && (
            <div data-testid="feedback-prompt" className="bg-slate-800/80 border border-blue-500/30 rounded-2xl p-5 sm:p-6 mt-4 animate-fade-in">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="text-sm font-semibold text-blue-400">
                  {t("feedback.prompt.title", lang)}
                </h3>
              </div>
              <p className="text-sm text-slate-400 mb-3">
                {t("feedback.prompt.body", lang)}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleStartFeedback}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors"
                >
                  {t("feedback.cta", lang)}
                </button>
                <button
                  onClick={handleDismissFeedbackPrompt}
                  className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
                >
                  {t("feedback.later", lang)}
                </button>
              </div>
            </div>
          )}



          {/* Tier 2 interstitial modal */}
          {showTier2Modal && (
            <div className="bg-slate-800/90 border border-blue-500/30 rounded-2xl p-6 mt-4 animate-fade-in">
              <h3 className="text-base font-semibold text-white mb-2">
                Quick feedback before you go?
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                You got this assessment for free -- help us make it better with a 2-3 minute chat.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTier2Modal(false);
                    setTier2ModalDismissed(true);
                    _doDownload();
                    handleStartFeedback();
                  }}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors"
                >
                  Start Feedback &amp; Download
                </button>
                <button
                  onClick={() => {
                    setShowTier2Modal(false);
                    setTier2ModalDismissed(true);
                    if (projectId) api.logEvent(projectId, "tier2_interstitial_dismissed");
                    _doDownload();
                  }}
                  className="text-slate-400 hover:text-slate-200 text-sm transition-colors"
                >
                  Just Download
                </button>
              </div>
            </div>
          )}

          {/* Feedback complete panel */}
          {feedbackParam && feedbackComplete && (
            <div className="bg-slate-800/80 border border-green-500/30 rounded-2xl p-5 sm:p-6 mt-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <h3 className="text-base font-semibold text-green-400">
                  {t("feedback.complete", lang)}
                </h3>
              </div>
              <div className="flex gap-3">
                {fullAssessment && (
                  <button
                    onClick={() => {
                      _doDownload();
                    }}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors shadow-lg"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Assessment
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl px-5 py-2.5 text-sm transition-colors"
                >
                  {t("feedback.back", lang)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Feedback floating button (suppress during pipeline phases) */}
      {!feedbackParam && !showBanner2 && !inPipelinePhase && projectId && messages.some((m) => m.role === "user") && (
        <QuickFeedbackButton projectId={projectId} language={lang} />
      )}

      {/* Retry error banner */}
      {sendError && (
        <div className="flex-shrink-0 border-t border-red-500/20 bg-red-950/30 px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <span className="text-red-400 text-sm">
              Something went wrong -- please try sending again
            </span>
            <button
              onClick={() => {
                const text = sendError;
                setSendError(null);
                handleSend(text);
              }}
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg px-3 py-1.5 text-sm transition-colors flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Input — hidden during pipeline phases (pipeline has its own chat input) and archived projects */}
      {!inPipelinePhase && !isArchived && (
        <ChatInput
          onSend={handleSend}
          disabled={inputDisabled}
          placeholder={
            feedbackComplete
              ? "Feedback complete. Thank you!"
              : feedbackParam || feedbackMode
              ? "Type your response..."
              : paidInterrogationActive
              ? "Type \"continue\" or share any additional context to resume..."
              : interrogationComplete
              ? "Your interrogation is complete. Generate your assessment above."
              : messages.length === 0
              ? t("chat.input.placeholder.first", lang)
              : t("chat.input.placeholder.reply", lang)
          }
          sendLabel={t("chat.send", lang)}
        />
      )}
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/* Partial Assessment Card                                              */
/* ------------------------------------------------------------------ */

function PartialAssessmentCard({ content }: { content: string }) {
  const parsed = parsePartialAssessment(content);

  const verdictColor =
    parsed.verdict === "PROCEED" ? "text-green-400 border-green-500/30"
    : parsed.verdict === "DO NOT PROCEED" ? "text-red-400 border-red-500/30"
    : parsed.verdict ? "text-amber-400 border-amber-500/30"
    : "";

  return (
    <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 sm:p-8 mt-6">
      {/* Verdict badge */}
      {parsed.verdict && (
        <div data-testid="verdict-badge" className={`inline-block border rounded-lg px-3 py-1.5 mb-4 ${verdictColor}`}>
          <span className="text-sm font-bold">{parsed.verdict}</span>
        </div>
      )}
      {parsed.verdictRationale && (
        <p className="text-slate-300 text-sm mb-6 leading-relaxed">{parsed.verdictRationale}</p>
      )}

      {/* Showcase dimension — full analysis */}
      {parsed.showcase && (
        <div data-testid="showcase-dimension" className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-3">{parsed.showcase.name}</h2>
          <div
            className="showcase-content prose prose-invert prose-sm max-w-none
              prose-p:text-slate-300 prose-p:leading-relaxed
              prose-strong:text-white"
            dangerouslySetInnerHTML={{ __html: simpleMarkdown(parsed.showcase.fullContent) }}
          />
        </div>
      )}

      {/* Skeleton entries — investigation agenda */}
      {parsed.skeletons.length > 0 && (
        <div className="border-t border-slate-700/50 pt-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            What the full assessment will investigate
          </h2>
          <div className="space-y-4">
            {parsed.skeletons.map((entry, i) => (
              <div
                key={i}
                data-testid="skeleton-entry"
                className="skeleton-entry bg-slate-800/60 border border-slate-700/40 rounded-xl p-4"
              >
                <h3 className="text-base font-medium text-slate-200 mb-1.5">{entry.name}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{entry.preview}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fallback: if parsing fails, render raw markdown */}
      {!parsed.showcase && parsed.skeletons.length === 0 && (
        <div
          className="prose prose-invert prose-sm max-w-none
            prose-h1:text-xl prose-h1:font-bold prose-h1:text-white prose-h1:mb-4
            prose-h2:text-lg prose-h2:font-semibold prose-h2:text-white prose-h2:mt-6 prose-h2:mb-3
            prose-h3:text-base prose-h3:font-medium prose-h3:text-slate-300 prose-h3:mt-4 prose-h3:mb-2
            prose-p:text-slate-300 prose-p:leading-relaxed
            prose-strong:text-white
            prose-hr:border-slate-700/50"
          dangerouslySetInnerHTML={{ __html: simpleMarkdown(content) }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Parse partial assessment (skeleton format) into structured data      */
/* ------------------------------------------------------------------ */

interface SkeletonEntry {
  name: string;
  preview: string;
}

interface ParsedPartialAssessment {
  verdict: string | null;
  verdictRationale: string;
  showcase: { name: string; fullContent: string } | null;
  skeletons: SkeletonEntry[];
}

function parsePartialAssessment(content: string): ParsedPartialAssessment {
  const result: ParsedPartialAssessment = {
    verdict: null,
    verdictRationale: "",
    showcase: null,
    skeletons: [],
  };

  // Parse verdict (new format: ## VERDICT)
  const verdictMatch = content.match(
    /##\s*VERDICT\s*\n+(PROCEED WITH CONDITIONS|PROCEED|DO NOT PROCEED)[^\n]*\n*([\s\S]*?)(?=\n##|\s*$)/i
  );
  if (verdictMatch) {
    result.verdict = verdictMatch[1].toUpperCase();
    result.verdictRationale = verdictMatch[2].trim();
  } else {
    // Fallback: old format (## Verdict: ...)
    const oldMatch = content.match(/##\s*Verdict:\s*(PROCEED|DO NOT PROCEED|PROCEED WITH CONDITIONS)/i);
    if (oldMatch) result.verdict = oldMatch[1].toUpperCase();
  }

  // Parse showcase dimension (## SHOWCASE: Name)
  const showcaseMatch = content.match(
    /##\s*SHOWCASE:\s*(.+?)\s*\n+([\s\S]*?)(?=\n##|\s*$)/i
  );
  if (showcaseMatch) {
    result.showcase = {
      name: showcaseMatch[1].trim(),
      fullContent: showcaseMatch[2].trim(),
    };
  }

  // Parse skeleton entries (### Name under ## SKELETON section)
  const skeletonSectionMatch = content.match(
    /##\s*SKELETON\s*\n+([\s\S]*?)(?=\n##\s*SKELETON\s+ENTRY\s+RULES|\n##\s*GENERAL\s+RULES|\s*$)/i
  );
  if (skeletonSectionMatch) {
    const skeletonText = skeletonSectionMatch[1];
    const entryPattern = /###\s*(.+?)\s*\n+([\s\S]*?)(?=\n###|\s*$)/g;
    let match;
    while ((match = entryPattern.exec(skeletonText)) !== null) {
      result.skeletons.push({
        name: match[1].trim(),
        preview: match[2].trim(),
      });
    }
  }

  return result;
}

/* ------------------------------------------------------------------ */
/* Helper: extract showcase dimension name from partial assessment      */
/* ------------------------------------------------------------------ */

function extractShowcaseDimension(content: string): string {
  // New format: ## SHOWCASE: Name
  const showcaseMatch = content.match(/##\s*SHOWCASE:\s*(.+)/i);
  if (showcaseMatch) return showcaseMatch[1].trim();

  // Fallback: old format — first ## heading after "Verdict" that isn't "Assessment Preview" or "SKELETON"
  const lines = content.split("\n");
  let foundVerdict = false;
  for (const line of lines) {
    if (/^##\s*Verdict/i.test(line) || /^##\s*VERDICT/i.test(line)) {
      foundVerdict = true;
      continue;
    }
    if (foundVerdict && /^##\s/.test(line) && !/Assessment Preview/i.test(line) && !/SKELETON/i.test(line)) {
      return line.replace(/^##\s*(?:SHOWCASE:\s*)?/, "").trim();
    }
  }
  return "Featured Dimension";
}

/* ------------------------------------------------------------------ */
/* Simple markdown to HTML converter                                    */
/* ------------------------------------------------------------------ */

function simpleMarkdown(md: string): string {
  // Strip HTML comments (section markers)
  let html = md.replace(/<!--[\s\S]*?-->/g, "");

  // Headers
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

  // Horizontal rules
  html = html.replace(/^---$/gm, "<hr />");

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Lists
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>)/g, "<ul>$1</ul>");
  // Clean up nested ul
  html = html.replace(/<\/ul>\s*<ul>/g, "");

  // Paragraphs: wrap lines that aren't already in tags
  html = html.replace(/^(?!<[h|u|l|o|p|d|t|b|hr])((?!\s*$).+)$/gm, "<p>$1</p>");

  return html;
}

/* ------------------------------------------------------------------ */
/* Progress indicator with rotating messages                           */
/* ------------------------------------------------------------------ */

const PHASE0_STEPS = [
  { key: "phase0.step1", delay: 0 },
  { key: "phase0.step2", delay: 3000 },
  { key: "phase0.step3", delay: 7000 },
  { key: "phase0.step4", delay: 12000 },
  { key: "phase0.step5", delay: 18000 },
];

const CONVERSATION_STEPS = [
  { key: "conversation.step1", delay: 0 },
  { key: "conversation.step2", delay: 4000 },
  { key: "conversation.step3", delay: 10000 },
];

const ASSESSMENT_STEPS = [
  { key: "assessment.step1", delay: 0 },
  { key: "assessment.step2", delay: 5000 },
  { key: "assessment.step3", delay: 15000 },
  { key: "assessment.step4", delay: 25000 },
  { key: "assessment.step5", delay: 40000 },
  { key: "assessment.step6", delay: 55000 },
  { key: "assessment.step7", delay: 70000 },
];

const STEPS_MAP = {
  phase0: PHASE0_STEPS,
  conversation: CONVERSATION_STEPS,
  assessment: ASSESSMENT_STEPS,
};

const NOTE_MAP = {
  phase0: "phase0.note",
  conversation: "conversation.note",
  assessment: "assessment.note",
};

function ProgressIndicator({
  type,
  language,
}: {
  type: "phase0" | "conversation" | "assessment";
  language: Language;
}) {
  const steps = STEPS_MAP[type];
  const noteKey = NOTE_MAP[type];

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i < steps.length; i++) {
      timers.push(
        setTimeout(() => setCurrentStep(i), steps[i].delay)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [steps]);

  return (
    <div className="flex justify-start mb-4">
      <div className="max-w-[85%] sm:max-w-[75%]">
        <div className="flex items-center gap-2 text-sm text-blue-400 mb-2">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="transition-opacity duration-300">
            {t(steps[currentStep].key, language)}
          </span>
        </div>
        <p className="text-xs text-slate-500 italic ml-6">
          {t(noteKey, language)}
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Full Generation Progress with timed messages                        */
/* ------------------------------------------------------------------ */

const FULL_GEN_STEPS = [
  { message: "Generating your full viability assessment...", delay: 0 },
  { message: "Analyzing all 10 dimensions in depth...", delay: 5000 },
  { message: "Building mathematical models and sensitivity analyses...", delay: 12000 },
  { message: "Compiling competitive intelligence dossier...", delay: 20000 },
  { message: "Generating risk register and recommendations...", delay: 30000 },
  { message: "Analyzing your decision patterns from the interrogation...", delay: 40000 },
  { message: "Finalizing assessment...", delay: 50000 },
  { message: "Still working -- our most advanced AI is being thorough...", delay: 120000 },
  { message: "Almost there -- wrapping up the final sections...", delay: 240000 },
];

function FullGenerationProgress() {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i < FULL_GEN_STEPS.length; i++) {
      timers.push(
        setTimeout(() => setCurrentStep(i), FULL_GEN_STEPS[i].delay)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="bg-slate-800/80 border border-blue-500/30 rounded-2xl p-6 mt-4 text-center">
      <div className="flex items-center justify-center gap-3 mb-2">
        <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-blue-400 font-medium transition-opacity duration-300">
          {FULL_GEN_STEPS[currentStep].message}
        </span>
      </div>
      <p className="text-xs text-slate-500">This may take a minute. Our most advanced AI is analyzing your entire interrogation.</p>
    </div>
  );
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
